import { createContext, useContext } from 'react';

/**
 * The client session store — "authenticated" means strictly "`GET /api/v1/me` returned 200"
 * (F5-a NFR-2, fail-closed). It mirrors the FS-3 permission-seam split: the types, the context and the
 * `useSession` hook live here (like `permissionContext.ts`); the thin `SessionProvider` that runs
 * the `/me` bootstrap lives beside it (like `PermissionProvider.tsx`).
 *
 * This is the compile surface F5-a WI-1.2 (login page) and F5-a WI-1.4 (verify) build on — the shapes below
 * are LOCKED by the plan's F5-a Decision 11 (by callable signature, not only by name).
 */

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

/** The signed-in user held in the store, mapped from `MeResponse.user`. */
export interface SessionUser {
    readonly id: number;
    readonly displayName: string;
    readonly email: string;
    /** `null` at the top of the reporting line. */
    readonly managerId: number | null;
}

/** One held permission key, mapped from a `MeResponse.permissions[]` entry. */
export interface SessionPermission {
    readonly key: string;
}

/**
 * A discriminated union on `status`, so a consumer cannot read `user`/`permissions` off a session
 * that has not resolved. Only the authenticated arm carries the user, the role, the held grants and
 * `hasPermission` — a membership test whose answer is drawn from the `permissions[]` SET: a key
 * absent from the array is not-held, never present-and-false (F5-a AC-15).
 */
export type SessionState =
    | { readonly status: 'loading' }
    | {
          readonly status: 'authenticated';
          readonly user: SessionUser;
          /** Display / effective-permissions input only — never an authorization decision (AD #26). */
          readonly role: string;
          /** The role's display label (`MeResponse.roleName`) — shown to the user; never a decision. */
          readonly roleName: string;
          readonly permissions: readonly SessionPermission[];
          /** `true` iff the key is present in `permissions[]`. */
          hasPermission(key: string): boolean;
      }
    | {
          readonly status: 'unauthenticated';
          /**
           * How the session became unauthenticated.
           *
           * - `'signout'` marks a *voluntary* sign-out, which the route guard uses to skip deep-link
           *   preservation so re-login lands on the default section rather than the page the user
           *   signed out from (BUG-005).
           * - `'expired'` marks an *involuntary* end of a session that WAS authenticated — a
           *   mid-session 401, or a cold-boot 401 on a browser that recorded a prior session
           *   ([[priorSessionMarker]]). `LoginRoute` renders the "your session expired" notice for
           *   this reason only (F5-a FR-6 / AC-8, BUG-007). The attempted route is still preserved, so
           *   re-auth lands the user back on it.
           * - `'unavailable'` marks a session bootstrap (`GET /api/v1/me`) that failed with something
           *   other than a 401 — the backend is down, answered 5xx, or the network failed. Nothing
           *   confirmed a session, so the store still fails closed to `unauthenticated` (F5-a NFR-2);
           *   `LoginRoute` says the service is unavailable rather than showing a plain sign-in page.
           *   The attempted route is preserved, as for an expiry.
           * - *Absent* for a first-visit / never-authenticated 401, which stays silent.
           */
          readonly reason?: 'signout' | 'expired' | 'unavailable';
          /**
           * The bootstrap's own failure, kept with `reason: 'unavailable'` so `LoginRoute`'s notice can show
           * the failed request's reference (`traceIdOf`, `TraceLine` — BUG-087's rule, BUG-212). `unknown`, as
           * a caught value is: an `ApiError` carries the problem body and its `traceId`; a network failure
           * carries none. Absent for every other reason.
           */
          readonly failedRequest?: unknown;
      };

/** The imperative half the provider adds on top of the state. */
export interface SessionActions {
    /** Re-run the `/me` bootstrap — used after a login lands (F5-a WI-1.2) to pick up the new session. */
    refresh(): Promise<void>;
    /**
     * `POST /logout`, then clear the store to `unauthenticated` (`reason: 'signout'`) once the server
     * has confirmed it — a success, or a 401 (the session was already gone). Any other failure leaves
     * the store authenticated and rejects, so the caller can say the sign-out did not complete (BUG-052).
     */
    signOut(): Promise<void>;
}

/** What `useSession` returns: the current state widened with the two actions (F5-a Decision 11). */
export type SessionContextValue = SessionState & SessionActions;

// `null` default, not a stub session: a consumer rendered with no provider is a wiring mistake to
// surface loudly (below), not a silent "logged out" that would flash the login page in a test.
export const SessionContext = createContext<SessionContextValue | null>(null);

/**
 * The current session plus `refresh`/`signOut`, from the nearest `SessionProvider`.
 *
 * Throws with no provider — every authenticated surface is mounted under the root `SessionProvider`
 * (`main.tsx`), so a missing one is a bug, not a runtime state to render around.
 */
export function useSession(): SessionContextValue {
    const value = useContext(SessionContext);
    if (value === null) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return value;
}
